import { useEffect, useState } from 'react';
import {
  IonButton, IonChip, IonIcon, IonInput, IonItem, IonNote, IonSelect,
  IonSelectOption, IonSpinner,
} from '@ionic/react';
import {
  cloudOfflineOutline,
  filmOutline,
  gridOutline,
  hardwareChipOutline,
  imageOutline,
  refreshOutline,
  videocamOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import {
  MEDIA_DEFAULT_ASPECT_RATIO,
  MEDIA_LIMITS,
  VIDEO_DEFAULT_DURATION_SECONDS,
  VIDEO_DURATIONS,
  type MediaAspectRatioId,
  type MediaAsset,
  type MediaKind,
  type PlatformId,
} from '@ai-media/shared';
import { aspectOptions, blockingPlatformLabels, isBusyAsset, toggleSelection } from '@/lib/media';
import { toast } from '@/store/ui.store';
import { useMediaStore } from '@/store/media.store';
import MediaCard from './MediaCard';

type KindFilter = '' | MediaKind;

interface MediaPickerProps {
  /** Library asset ids currently attached to the post, in pick order. */
  selectedIds: string[];
  onChange: (ids: string[]) => void;
  /** Channels the post targets — picked media is validated against their rules. */
  platforms?: PlatformId[];
}

const ASPECT_OPTIONS = aspectOptions();
const COUNT_OPTIONS = Array.from({ length: MEDIA_LIMITS.maxAssetsPerRequest }, (_, index) => index + 1);

/**
 * Generate images and videos, then pick finished assets from the library list.
 * The selected ids travel with `POST /content/posts` as `mediaAssetIds` and are
 * resolved to URLs (and usage-tracked) server-side.
 */
export default function MediaPicker({ selectedIds, onChange, platforms = [] }: MediaPickerProps) {
  const { t } = useTranslation();
  const {
    assets,
    loading,
    generating,
    filters,
    health,
    healthLoading,
    attaching,
    load,
    setFilters,
    refreshHealth,
    generateImage,
    generateVideo,
    remove,
  } = useMediaStore();

  const [prompt, setPrompt] = useState('');
  const [provider, setProvider] = useState('worker');
  const [aspectRatio, setAspectRatio] = useState<MediaAspectRatioId>(MEDIA_DEFAULT_ASPECT_RATIO);
  const [count, setCount] = useState(1);
  const [durationSeconds, setDurationSeconds] = useState<number>(VIDEO_DEFAULT_DURATION_SECONDS);

  // Store actions are stable, so this loads the library once on mount.
  useEffect(() => {
    void load();
    void refreshHealth();
  }, [load, refreshHealth]);

  const workerOnline = health?.worker.healthy ?? null;
  const pickedAssets = assets.filter((asset) => selectedIds.includes(asset.id));
  const blockedLabels = blockingPlatformLabels(pickedAssets, platforms);

  const assertPrompt = (): boolean => {
    if (prompt.trim().length < 3) {
      toast(t('media.needPrompt'), 'warning');
      return false;
    }
    return true;
  };

  const selectAsset = (asset: MediaAsset) => {
    const result = toggleSelection(selectedIds, asset.id);
    if (!result.added && !selectedIds.includes(asset.id)) {
      toast(t('media.maxReached', { max: MEDIA_LIMITS.maxMediaPerPost }), 'warning');
      return;
    }
    onChange(result.ids);
  };

  /** Ready images jump onto the post straight away; unselecting stays one tap. */
  const onGenerateImage = async () => {
    if (!assertPrompt()) return;
    const created = await generateImage({ prompt: prompt.trim(), aspectRatio, count, provider });
    let next = selectedIds;
    for (const asset of created.filter((item) => item.status === 'ready')) {
      const result = toggleSelection(next, asset.id);
      if (!result.added) {
        toast(t('media.maxReached', { max: MEDIA_LIMITS.maxMediaPerPost }), 'warning');
        break;
      }
      next = result.ids;
    }
    if (next !== selectedIds) onChange(next);
  };

  const onGenerateVideo = async () => {
    if (!assertPrompt()) return;
    const created = await generateVideo({ prompt: prompt.trim(), aspectRatio, durationSeconds, provider });
    // A queued render is picked from the list once it flips to `ready`.
    if (!created || created.status !== 'ready') return;
    const result = toggleSelection(selectedIds, created.id);
    if (result.added) onChange(result.ids);
  };

  const applyKind = (kind: KindFilter) => {
    setFilters({ kind: kind === '' ? undefined : kind });
    void load();
  };

  const refresh = () => {
    void load();
    void refreshHealth();
  };

  return (
    <div className="media-picker">
      <div className={`media-picker__health${workerOnline === false ? ' media-picker__health--offline' : ''}`}>
        <IonIcon icon={workerOnline === false ? cloudOfflineOutline : hardwareChipOutline} />
        <span>
          {healthLoading || workerOnline === null
            ? t('media.gpu.checking')
            : workerOnline
              ? t('media.gpu.online')
              : t('media.gpu.offline')}
        </span>
      </div>

      <IonItem lines="none">
        <textarea
          data-testid="media-prompt"
          aria-label={t('media.prompt')}
          className="media-picker__prompt"
          placeholder={t('media.promptPlaceholder')}
          value={prompt}
          onChange={(event) => setPrompt(event.target.value)}
        />
      </IonItem>

      <IonItem lines="none">
        <IonSelect
          data-testid="media-provider"
          label={t('media.provider', { defaultValue: 'Provider' })}
          interface="popover"
          value={provider}
          onIonChange={(event) => setProvider(String(event.detail.value))}
        >
          <IonSelectOption value="worker">worker</IonSelectOption>
          <IonSelectOption value="fal">fal.ai models</IonSelectOption>
          <IonSelectOption value="fal.ai">fal.ai</IonSelectOption>
          <IonSelectOption value="ltx">ltx</IonSelectOption>
        </IonSelect>
      </IonItem>

      <div className="media-picker__row">
        <IonItem lines="none">
          <IonSelect
            label={t('media.aspectRatio')}
            interface="popover"
            value={aspectRatio}
            onIonChange={(event) => setAspectRatio(event.detail.value as MediaAspectRatioId)}
          >
            {ASPECT_OPTIONS.map((option) => (
              <IonSelectOption key={option.id} value={option.id}>
                {option.label} · {option.hint}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
        <IonItem lines="none">
          <IonSelect
            label={t('media.count')}
            interface="popover"
            value={count}
            onIonChange={(event) => setCount(Number(event.detail.value))}
          >
            {COUNT_OPTIONS.map((n) => (
              <IonSelectOption key={n} value={n}>
                {n}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
        <IonItem lines="none">
          <IonSelect
            label={t('media.duration')}
            interface="popover"
            value={durationSeconds}
            onIonChange={(event) => setDurationSeconds(Number(event.detail.value))}
          >
            {VIDEO_DURATIONS.map((n) => (
              <IonSelectOption key={n} value={n}>
                {t('media.seconds', { n })}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
      </div>

      <div className="media-picker__actions">
        <IonButton disabled={generating !== ''} onClick={() => void onGenerateImage()}>
          {generating === 'image' ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={imageOutline} />}
          {t('media.generateImage')}
        </IonButton>
        <IonButton fill="outline" disabled={generating !== ''} onClick={() => void onGenerateVideo()}>
          {generating === 'video' ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={videocamOutline} />}
          {t('media.generateVideo')}
        </IonButton>
      </div>

      <div className="media-picker__filters">
        <IonChip
          color={filters.kind === undefined ? 'primary' : 'medium'}
          outline={filters.kind !== undefined}
          onClick={() => applyKind('')}
        >
          <IonIcon icon={gridOutline} />
          <IonNote>{t('media.filterAll')}</IonNote>
        </IonChip>
        <IonChip
          color={filters.kind === 'image' ? 'primary' : 'medium'}
          outline={filters.kind !== 'image'}
          onClick={() => applyKind('image')}
        >
          <IonIcon icon={imageOutline} />
          <IonNote>{t('media.filterImages')}</IonNote>
        </IonChip>
        <IonChip
          color={filters.kind === 'video' ? 'primary' : 'medium'}
          outline={filters.kind !== 'video'}
          onClick={() => applyKind('video')}
        >
          <IonIcon icon={filmOutline} />
          <IonNote>{t('media.filterVideos')}</IonNote>
        </IonChip>
        <IonChip outline disabled={loading} onClick={refresh}>
          <IonIcon icon={refreshOutline} />
          <IonNote>{t('media.refresh')}</IonNote>
        </IonChip>
      </div>

      {loading && assets.length === 0 && <IonSpinner name="crescent" />}

      {assets.length > 0 && (
        <div className="media-picker__grid">
          {assets.map((asset) => (
            <MediaCard
              key={asset.id}
              asset={asset}
              selectable
              selected={selectedIds.includes(asset.id)}
              busy={attaching === asset.id}
              progressLabel={asset.status === 'queued' ? t('media.status.queued') : undefined}
              onSelect={selectAsset}
              onDelete={(deleted) => void remove(deleted.id)}
            />
          ))}
        </div>
      )}

      {assets.length === 0 && !loading && (
        <div className="media-picker__empty">
          <IonIcon icon={filters.kind === 'video' ? filmOutline : imageOutline} />
          <strong>{t('media.empty')}</strong>
          <span>{t('media.emptyHint')}</span>
        </div>
      )}

      <div className="media-picker__footer">
        <IonNote>{t('media.selectedCount', { count: selectedIds.length, max: MEDIA_LIMITS.maxMediaPerPost })}</IonNote>
        {pickedAssets.some(isBusyAsset) && <IonNote color="warning">{t('media.pendingHint')}</IonNote>}
      </div>

      {blockedLabels.length > 0 && (
        <IonNote color="warning" className="media-picker__blocked">
          {t('media.blocked', { platforms: blockedLabels.join(', ') })}
        </IonNote>
      )}
    </div>
  );
}